import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { CiTrash } from "react-icons/ci";

import css from "./SingleMenuDishComponent.module.css";
import { ImageReusable } from "../../../ui";
import { cycleDayMenuActions, menuMenuActions } from "../../../store/slices/menu-slices";
import { Pencil, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { SelectProteinDishModal } from "../menuSectionModals/SelectProteinDishModal/SelectProteinDishModal";
import { SelectSauceDishModal } from "../menuSectionModals/SelectSauceDishModal/SelectSauceDishModal";
import { menuMenuService } from "../../../services/menu-services/menuMenu.service";
import { cycleMenuService } from "../../../services/menu-services/cycleMenu.service";
import { Button } from "antd";
import { DishType } from "../../../constants/enums";

const DishMiniCard = ({
	dish,
	label,
	variant,
	currentLanguage,
	onClick,
	onDelete,
	isDeleting,
}) => {
	const {
		name,
		img,
		energy,
		proteins,
		fats,
		carbs,
		weight,
		weightNetto,
		cost,
	} = dish;
	const displayName = name?.[currentLanguage] || name?.["en"] || "Unknown Dish";

	return (
		<div
			className={`${css.dishMiniCardOuter} ${variant === "protein" ? css.dishMiniCardProtein : css.dishMiniCardSauce}`}
			onClick={onClick}
			role="button"
			tabIndex={0}
			onKeyDown={(e) => e.key === "Enter" && onClick(e)}
		>
			<span className={css.dishMiniCardLabel}>{label}</span>
			<div className={css.dishMiniCardWrap}>
				<div className={css.imgBlock}>
					<ImageReusable src={img} className={css.imageTag} alt={displayName} />
				</div>
				<div className={css.nameBlock}>
					<div className={css.dishName}>{displayName}</div>
					<div className={css.nutritionGrid}>
						<div className={css.nutritionItem}>
							<span className={css.nutritionLabel}>Energy:</span>
							<span className={css.nutritionValue}>{energy || 0} kcal</span>
						</div>
						<div className={css.nutritionItem}>
							<span className={css.nutritionLabel}>P:</span>
							<span className={css.nutritionValue}>{proteins || 0} g</span>
						</div>
						<div className={css.nutritionItem}>
							<span className={css.nutritionLabel}>F:</span>
							<span className={css.nutritionValue}>{fats || 0} g</span>
						</div>
						<div className={css.nutritionItem}>
							<span className={css.nutritionLabel}>C:</span>
							<span className={css.nutritionValue}>{carbs || 0} g</span>
						</div>
						<div className={css.nutritionItem}>
							<span className={css.nutritionLabel}>W:</span>
							<span className={css.nutritionValue}>{weight || 0} g</span>
						</div>
						<div className={css.nutritionItem}>
							<span className={css.nutritionLabel}>W.N.</span>
							<span className={css.nutritionValue}>{weightNetto || 0} g</span>
						</div>
					</div>
					{onDelete && (
						<div className={css.actionRow}>
							<button
								type="button"
								className={css.miniCardDeleteButton}
								onClick={onDelete}
								disabled={isDeleting}
							>
								<CiTrash className={css.deleteIcon} /> Delete
							</button>
						</div>
					)}
				</div>
				<div className={css.price}>{cost}</div>
			</div>
		</div>
	);
};

const SingleMenuDishComponent = ({
	dish,
	menuId,
	mealName,
	type,
	proteinDish,
	sauceDish,
	setIsOpen,
	idMatching,
	mealId,
	packId,
	isCycleMode = false,
}) => {
	const dispatch = useDispatch();
	const { i18n, t } = useTranslation();
	const [isDeleting, setIsDeleting] = useState(false);
	const [isDeletingProtein, setIsDeletingProtein] = useState(false);
	const [isDeletingSauce, setIsDeletingSauce] = useState(false);
	const [isProteinModalOpen, setIsProteinModalOpen] = useState(false);
	const [isSauceModalOpen, setIsSauceModalOpen] = useState(false);
	const { dataOfDay } = useSelector((store) => store.availableDishes);

	if (!dish) {
		return (
			<div className={css.wrap}>
				<div className={css.nameBlock}>No Dish</div>
			</div>
		);
	}

	const {
		name,
		img,
		energy,
		proteins,
		fats,
		carbs,
		weight,
		weightNetto,
		cost,
	} = dish;
	const currentLanguage = i18n.language;
	const displayName = name?.[currentLanguage] || name?.["en"] || "Unknown Dish";

	const handleDeleteMenuDish = async (event) => {
		event.stopPropagation();
		event.preventDefault();

		if (!menuId || isDeleting) {
			return;
		}

		try {
			setIsDeleting(true);
			if (isCycleMode) {
				await cycleMenuService.deleteById(menuId);
				dispatch(cycleDayMenuActions.changeTrigger());
			} else {
				await dispatch(menuMenuActions.deleteMenuDish({ menuId })).unwrap();
			}
			toast.success(
				t("toast-menu-dish-deleted", {
					defaultValue: "Dish removed from menu",
				}),
			);
		} catch (error) {
			toast.error(
				error?.message ||
					t("toast-menu-dish-delete-failed", {
						defaultValue: "Failed to remove dish from menu",
					}),
			);
		} finally {
			setIsDeleting(false);
		}
	};

	const handleDeleteProteinDish = async (event) => {
		event.stopPropagation();
		event.preventDefault();
		if (!menuId || isDeletingProtein) return;
		try {
			setIsDeletingProtein(true);
			if (isCycleMode) {
				await cycleMenuService.updateById(menuId, { proteinDishId: null });
				dispatch(cycleDayMenuActions.changeTrigger());
			} else {
				await menuMenuService.updateMenuPack(menuId, {
					proteinDishId: null,
					packId,
					mealId,
					dishId: dish.id,
					date: dataOfDay,
				});
				dispatch(menuMenuActions.changeTrigger());
			}
			toast.success(
				t("toast-protein-dish-removed", {
					defaultValue: "Protein dish removed",
				}),
			);
		} catch (error) {
			toast.error(
				error?.message ||
					t("toast-protein-dish-remove-failed", {
						defaultValue: "Failed to remove protein dish",
					}),
			);
		} finally {
			setIsDeletingProtein(false);
		}
	};

	const handleDeleteSauceDish = async (event) => {
		event.stopPropagation();
		event.preventDefault();
		if (!menuId || isDeletingSauce) return;
		try {
			setIsDeletingSauce(true);
			if (isCycleMode) {
				await cycleMenuService.updateById(menuId, { sauceDishId: null });
				dispatch(cycleDayMenuActions.changeTrigger());
			} else {
				await menuMenuService.updateMenuPack(menuId, {
					sauceDishId: null,
					packId,
					mealId,
					dishId: dish.id,
					date: dataOfDay,
				});
				dispatch(menuMenuActions.changeTrigger());
			}
			toast.success(
				t("toast-sauce-dish-removed", { defaultValue: "Sauce dish removed" }),
			);
		} catch (error) {
			toast.error(
				error?.message ||
					t("toast-sauce-dish-remove-failed", {
						defaultValue: "Failed to remove sauce dish",
					}),
			);
		} finally {
			setIsDeletingSauce(false);
		}
	};

	const openModal = () => {
		if (isCycleMode) {
			dispatch(cycleDayMenuActions.setActivePackId(packId));
			dispatch(cycleDayMenuActions.setActiveMealId(mealId));
			dispatch(cycleDayMenuActions.setActiveIdMatching(idMatching || null));
		} else {
			dispatch(menuMenuActions.changeId(packId));
			dispatch(menuMenuActions.changeIdForMeal(mealId));
			if (idMatching) {
				dispatch(menuMenuActions.changeIdMatching(idMatching));
				dispatch(menuMenuActions.changeTrigger());
			}
		}
		setIsOpen(true);
	};

	return (
		<>
			{type === DishType.BASE && (
				<div className={css.baseDish}>{t("Base Dish")}</div>
			)}
			<div className={css.wrap}>
				<div className={css.imgBlock}>
					<ImageReusable src={img} className={css.imageTag} alt="Food Image" />
				</div>

				<div className={css.nameBlock}>
					<div className={css.dishName}>{displayName}</div>
					{/* {menuId && (
          <button
            type="button"
            className={css.deleteButton}
            onClick={handleDeleteMenuDish}
            title={t("delete-text", { defaultValue: "Delete" })}
            disabled={isDeleting}
          >
            <CiTrash className={css.deleteIcon} />
          </button>
        )} */}

					<div className={css.nutritionGrid}>
						<div className={css.nutritionItem}>
							<span className={css.nutritionLabel}>Energy:</span>
							<span className={css.nutritionValue}>{energy || 0} kcal</span>
						</div>
						<div className={css.nutritionItem}>
							<span className={css.nutritionLabel}>P:</span>
							<span className={css.nutritionValue}>{proteins || 0} g</span>
						</div>
						<div className={css.nutritionItem}>
							<span className={css.nutritionLabel}>F:</span>
							<span className={css.nutritionValue}>{fats || 0} g</span>
						</div>
						<div className={css.nutritionItem}>
							<span className={css.nutritionLabel}>C:</span>
							<span className={css.nutritionValue}>{carbs || 0} g</span>
						</div>
						<div className={css.nutritionItem}>
							<span className={css.nutritionLabel}>W:</span>
							<span className={css.nutritionValue}>{weight || 0} g</span>
						</div>
						<div className={css.nutritionItem}>
							<span className={css.nutritionLabel}>W.N.</span>
							<span className={css.nutritionValue}>{weightNetto || 0} g</span>
						</div>
					</div>
					<div className={css.actionRow}>
						{menuId && (
							<button
								type="button"
								className={css.deleteButton}
								onClick={handleDeleteMenuDish}
								title={t("delete-text", { defaultValue: "Delete" })}
								disabled={isDeleting}
							>
								<CiTrash className={css.deleteIcon} /> Delete
							</button>
						)}
						<Link to={`/dishes/update-dish/${dish.id}`} asChild>
							<button type="button" className={css.updateButton}>
								<Pencil size={16} /> Update
							</button>
						</Link>
					</div>
				</div>
				<div className={css.meal}>{mealName}</div>
				<div className={css.price}>{cost}</div>
			</div>

			{type === DishType.BASE && proteinDish && (
				<DishMiniCard
					dish={proteinDish}
					label={t("Protein Dish")}
					variant="protein"
					currentLanguage={currentLanguage}
					onClick={(e) => {
						e.stopPropagation();
						e.preventDefault();
						setIsProteinModalOpen(true);
					}}
					onDelete={menuId ? handleDeleteProteinDish : undefined}
					isDeleting={isDeletingProtein}
				/>
			)}

			{type === DishType.BASE && sauceDish && (
				<DishMiniCard
					dish={sauceDish}
					label={t("Sauce Dish")}
					variant="sauce"
					currentLanguage={currentLanguage}
					onClick={(e) => {
						e.stopPropagation();
						e.preventDefault();
						setIsSauceModalOpen(true);
					}}
					onDelete={menuId ? handleDeleteSauceDish : undefined}
					isDeleting={isDeletingSauce}
				/>
			)}

			<div
				style={{ paddingLeft: 16, display: "flex", gap: 8, flexWrap: "wrap" }}
			>
				{type === DishType.BASE && !proteinDish && (
					<button
						type="button"
						className={css.proteinDishEmpty}
						onClick={(e) => {
							e.stopPropagation();
							e.preventDefault();
							setIsProteinModalOpen(true);
						}}
					>
						{t("+ Select Protein Dish")}
					</button>
				)}
				{type === DishType.BASE && !sauceDish && (
					<button
						type="button"
						className={css.sauceDishEmpty}
						onClick={(e) => {
							e.stopPropagation();
							e.preventDefault();
							setIsSauceModalOpen(true);
						}}
					>
						{t("+ Select Sauce Dish")}
					</button>
				)}

				<Button icon={<Plus size={16} />} onClick={openModal}>
					{t("Add Dish")}
				</Button>
			</div>

			{isProteinModalOpen && (
				<SelectProteinDishModal
					isOpen={isProteinModalOpen}
					onClose={() => setIsProteinModalOpen(false)}
					menuId={menuId}
					currentProteinDish={proteinDish}
					packId={packId}
					mealId={mealId}
					dishId={dish.id}
					proteinDish={proteinDish}
					onSaveOverride={isCycleMode ? async (selectedDish) => {
						await cycleMenuService.updateById(menuId, { proteinDishId: selectedDish.id });
						dispatch(cycleDayMenuActions.changeTrigger());
					} : undefined}
					onRemoveOverride={isCycleMode ? async () => {
						await cycleMenuService.updateById(menuId, { proteinDishId: null });
						dispatch(cycleDayMenuActions.changeTrigger());
					} : undefined}
				/>
			)}

			{isSauceModalOpen && (
				<SelectSauceDishModal
					isOpen={isSauceModalOpen}
					onClose={() => setIsSauceModalOpen(false)}
					menuId={menuId}
					currentSauceDish={sauceDish}
					packId={packId}
					mealId={mealId}
					dishId={dish.id}
					sauceDish={sauceDish}
					onSaveOverride={isCycleMode ? async (selectedDish) => {
						await cycleMenuService.updateById(menuId, { sauceDishId: selectedDish.id });
						dispatch(cycleDayMenuActions.changeTrigger());
					} : undefined}
					onRemoveOverride={isCycleMode ? async () => {
						await cycleMenuService.updateById(menuId, { sauceDishId: null });
						dispatch(cycleDayMenuActions.changeTrigger());
					} : undefined}
				/>
			)}
		</>
	);
};

export { SingleMenuDishComponent };
